import React from 'react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { When } from 'react-if'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { MediaThumbnail } from '@/molecules/MediaCard/MediaThumbnail'
import { Media } from '@/utils/EpisodeUtil'
import { EpisodeDescription } from '@/views/WatchProjectTabsView/WatchEpisodesTab/EpisodeDescription'
import { MediaDescriptors } from './MediaDescriptors'

export interface MediaCardProps extends Media {
  onCalendarClick?: () => void
  onClick?: () => void
  userHasEarlyAccess?: boolean
  hasWatchableReasons?: boolean
  thumbnailClassName?: string
  theaterEpisodeCard?: boolean
}

const earlyAccessHoursBeforeConfig = {
  [slugs.theChosen]: 24,
  [slugs.wingfeatherSaga]: 48,
  [slugs.tuttleTwins]: 96,
}

interface UseMediaDatesArgs {
  earlyAccessDate?: string
  publiclyAvailableDate?: string
  unavailableReason?: string
  userHasEarlyAccess?: boolean
}

interface MediaDatesReturn {
  canStreamEarlyAccess: boolean
  canStreamEpisode: boolean
  canStreamFreeEpisode: boolean
  earlyAccessDateJS?: Date
  earlyAccessDateTime?: DateTime
  publicAvailableDateJS?: Date
  userHasEarlyAccess?: boolean
}

const useMediaDates = ({
  earlyAccessDate,
  publiclyAvailableDate,
  userHasEarlyAccess,
}: UseMediaDatesArgs): MediaDatesReturn => {
  const now = new Date()

  const publicAvailableDateJS = publiclyAvailableDate ? new Date(publiclyAvailableDate) : undefined
  const earlyAccessDateJS = earlyAccessDate ? new Date(earlyAccessDate) : undefined

  const earlyAccessDateTime = earlyAccessDateJS ? DateTime.fromJSDate(earlyAccessDateJS) : undefined

  const canStreamFreeEpisode = !!publicAvailableDateJS && publicAvailableDateJS <= now
  const canStreamEarlyAccess = !!earlyAccessDateJS && earlyAccessDateJS <= now && !canStreamFreeEpisode
  const canStreamEpisode = true

  return {
    publicAvailableDateJS,
    earlyAccessDateJS,
    earlyAccessDateTime,
    canStreamFreeEpisode,
    canStreamEarlyAccess,
    canStreamEpisode,
    userHasEarlyAccess,
  }
}

export const MediaCard: React.FC<MediaCardProps> = ({
  activeDate,
  aspectClassName = 'aspect-w-16 aspect-h-9',
  bottomBackgroundGradientClass,
  className = '',
  description,
  displayDescription = true,
  earlyAccessDate,
  episodeSlug,
  firstEarlyAccessEpisode = false,
  hasEarlyAccess = false,
  href,
  imgPriority,
  includeHoverScale = true,
  objectPositionClassName = 'object-left',
  onClick,
  poster,
  posterTransformation = 'c_fill,q_auto,ar_16:9,g_north,w_480,f_webp',
  prerelease = false,
  progress,
  duration,
  projectSlug,
  projectType,
  publiclyAvailableDate,
  subtitle,
  title,
  disableLink = false,
  unavailableReason,
  userHasEarlyAccess,
  guid,
  hasWatchableReasons = false,
  watchableAvailabilityStatus = null,
  thumbnailClassName = '',
  episodeNumber,
  theaterEpisodeCard = false,
  watchableType,
}) => {
  const {
    canStreamEarlyAccess,
    canStreamEpisode: computedCanStreamEpisode,
    canStreamFreeEpisode,
    earlyAccessDateJS,
    publicAvailableDateJS,
  } = useMediaDates({
    earlyAccessDate,
    publiclyAvailableDate,
    unavailableReason,
    userHasEarlyAccess,
  })
  const canStreamEpisode = !disableLink && computedCanStreamEpisode

  if (!poster) return null

  const earlyAccessHoursBefore = earlyAccessHoursBeforeConfig[projectSlug] ?? 0

  const thumbnailElement = (
    <MediaThumbnail
      activeDate={activeDate}
      aspectClassName={aspectClassName}
      bottomBackgroundGradientClass={bottomBackgroundGradientClass}
      canStreamEarlyAccess={canStreamEarlyAccess}
      canStreamEpisode={canStreamEpisode}
      canStreamFreeEpisode={canStreamFreeEpisode}
      earlyAccessDate={earlyAccessDateJS}
      earlyAccessHoursBefore={earlyAccessHoursBefore}
      episodeSlug={episodeSlug}
      firstEarlyAccessEpisode={firstEarlyAccessEpisode}
      hasEarlyAccess={hasEarlyAccess}
      href={href}
      imgPriority={imgPriority}
      includeHoverScale={includeHoverScale}
      objectPositionClassName={objectPositionClassName}
      poster={poster}
      posterTransformation={posterTransformation}
      prerelease={prerelease}
      progress={progress}
      projectSlug={projectSlug}
      projectType={projectType}
      publiclyAvailableDate={publicAvailableDateJS}
      title={title}
      unavailableReason={unavailableReason}
      userHasEarlyAccess={userHasEarlyAccess}
      hasWatchableReasons={hasWatchableReasons}
      watchableAvailabilityStatus={watchableAvailabilityStatus}
      guid={guid}
      className={thumbnailClassName}
    />
  )

  const episodeDescriptionElement = theaterEpisodeCard ? (
    <EpisodeDescription
      title={title}
      subtitle={subtitle}
      displayDescription={displayDescription}
      description={description}
      href={href}
      episodeNumber={episodeNumber}
      duration={duration}
      projectType={projectType}
      prerelease={prerelease}
      watchableType={watchableType}
      isWrappedInLink
    />
  ) : null

  // When theaterEpisodeCard is true and content is streamable, wrap both thumbnail and description in a single link
  if (theaterEpisodeCard && canStreamEpisode) {
    return (
      <>
        <InternalLink
          href={href}
          className="cursor-pointer"
          aria-label={href.includes('/community/stories') ? 'video-story-link' : 'episode-link'}
          onClick={onClick}
        >
          <div
            aria-label={href.includes('/community/stories') ? 'video-story' : 'episode'}
            className={classNames(['text-white relative', className])}
          >
            <div className="relative">{thumbnailElement}</div>
            {episodeDescriptionElement}
          </div>
        </InternalLink>
        <CaptionSM color="core-gray-500" className="mt-1 line-clamp-4 md:hidden">
          {description}
        </CaptionSM>
      </>
    )
  }

  return (
    <>
      <div
        aria-label={href.includes('/community/stories') ? 'video-story' : 'episode'}
        className={classNames(['text-white relative group', className])}
      >
        <div className={classNames('relative', { 'cursor-pointer': canStreamEpisode })}>
          {canStreamEpisode ? (
            <InternalLink
              href={href}
              className="cursor-pointer"
              aria-label={href.includes('/community/stories') ? 'video-story-link' : 'episode-link'}
              onClick={onClick}
            >
              {thumbnailElement}
            </InternalLink>
          ) : (
            thumbnailElement
          )}
        </div>
        <When condition={theaterEpisodeCard}>
          <EpisodeDescription
            title={title}
            subtitle={subtitle}
            displayDescription={displayDescription}
            description={description}
            href={href}
            episodeNumber={episodeNumber}
            duration={duration}
            projectType={projectType}
            prerelease={prerelease}
            watchableType={watchableType}
          />
        </When>
        <When condition={!theaterEpisodeCard}>
          <MediaDescriptors
            title={title}
            subtitle={subtitle}
            displayDescription={displayDescription}
            description={description}
            href={href}
          />
        </When>
      </div>
      <When condition={theaterEpisodeCard}>
        <CaptionSM color="core-gray-500" className="mt-1 line-clamp-4 md:hidden">
          {description}
        </CaptionSM>
      </When>
    </>
  )
}
